import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export function AddressMap({ coords }) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    mapRef.current = new maplibregl.Map({
      container: mapContainerRef.current,
      style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
      center: coords ? [coords[1], coords[0]] : [10.4515, 51.1657],
      zoom: coords ? 15 : 4.5,
      attributionControl: false,
    });
    mapRef.current.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    if (coords) {
      markerRef.current = new maplibregl.Marker({ color: '#29beff' })
        .setLngLat([coords[1], coords[0]])
        .addTo(mapRef.current);
    }
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !coords) return;
    if (markerRef.current) markerRef.current.remove();
    markerRef.current = new maplibregl.Marker({ color: '#29beff' })
      .setLngLat([coords[1], coords[0]])
      .addTo(mapRef.current);
    mapRef.current.flyTo({ center: [coords[1], coords[0]], zoom: 16, duration: 800 });
  }, [coords]);

  return (
    <div className="h-full w-full rounded-lg overflow-hidden border border-gray-200">
      <div ref={mapContainerRef} className="w-full h-full" />
    </div>
  );
}

export default function AddressMapPicker({ value, onChange, onCoordsChange, inputClass, placeholder }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchAddress = useCallback(async (text) => {
    if (text.length < 3) { setSuggestions([]); return; }
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(text)}&countrycodes=de,at,ch&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': 'de' } }
      );
      const data = await res.json();
      setSuggestions(data);
      setShowDropdown(data.length > 0);
    } catch { setSuggestions([]); }
    finally { setIsSearching(false); }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAddress(val), 350);
  };

  const handleSelect = (item) => {
    setQuery(item.display_name);
    onChange(item.display_name);
    onCoordsChange?.([parseFloat(item.lat), parseFloat(item.lon)]);
    setShowDropdown(false);
    setSuggestions([]);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder || 'Adresse eingeben...'}
          className={inputClass}
          data-testid="gegner-schadenort"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isSearching ? (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-52 overflow-y-auto" style={{ top: '100%' }}>
          {suggestions.map((item) => (
            <button
              key={item.place_id}
              onClick={() => handleSelect(item)}
              className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-start gap-2 border-b border-gray-50 last:border-0 transition-colors"
            >
              <MapPin className="w-4 h-4 text-[#29beff] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700 leading-snug">{item.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
